/**
 * The assign popover on the work-order record.
 *
 * ===========================================================================
 * NO VERIFIED FIGMA NODE BACKS THIS CONTROL (ESC-3c, owner-ruled)
 * ===========================================================================
 * The owner ruled it built "to the API contract and house style", and stated
 * plainly that the visual detail is NOT transcribed from a verified node the way
 * the board's columns and the DL-file panels are. So everything here is HOUSE
 * STYLE — the same Radix dropdown, the same 264px width, the same row shape and
 * the same truncation sentence as the bulk Assign menu — and nothing here claims
 * a measurement. The house style is itself a verified system; this control
 * inherits from it rather than inventing.
 *
 * ===========================================================================
 * ONE DIRECTORY, NOT A SECOND COPY OF ONE
 * ===========================================================================
 * The search, the cap and the "showing N of M" honesty all come from
 * `features/directory` — the module the bulk menu uses. Re-authoring them here is
 * exactly how one surface keeps a fix the other never gets.
 *
 * ===========================================================================
 * ⚠ BUT THE VERDICT IS LOCAL, AND THIS PARAGRAPH USED TO GET IT WRONG (F-103)
 * ===========================================================================
 * It read: "the out-of-scope prediction all come from `features/directory` … What
 * is local is the COPY, because the consequence differs: assigning an inquiry to
 * somebody outside the country hides an inquiry; assigning WORK to them parks a
 * development job with a person who cannot open it."
 *
 * NOTHING IS PARKED. The two surfaces do not merely differ in consequence — they
 * differ in whether the server accepts at all:
 *
 *   · inquiries — `POST …/inquiries/bulk` WRITES the assignment and answers
 *     `warnings:[{id, code}]`. Out-of-scope is a real, accepted, flagged state, so
 *     "will be accepted, but…" is true there and the confirm asks a real question.
 *   · work orders — `work-orders.service.ts` requires an ACTIVE user holding a role
 *     row in THIS order's country and answers 404 otherwise, with one body for
 *     absent, foreign and disabled alike. There is no accepted-but-flagged state.
 *
 * So `features/directory`'s `outOfScope` is the INQUIRIES rule and stays that way —
 * it is asserted by `assign-colleague.test.tsx` against the real implementation, and
 * changing it would break a surface where it is correct. What this surface needs is
 * its own verdict, {@link isAssignableHere}, which is the server's join restated:
 * a role row in this country, group admin or not.
 */

import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ChevronDown } from 'lucide-react';

import type { CountryCode } from '../../domain/countries';
import { Button } from '../../components/ui';
import { UserAvatar } from '../avatars/UserAvatar';
import { MIN_COLLEAGUE_SEARCH } from '../directory/api';
import { useColleagues, useDebouncedTerm } from '../directory/use-directory';
import type { Colleague } from '../directory/use-directory';

/**
 * Same row primitive as the bulk menu's, re-declared rather than imported from
 * `features/inquiries/Toolbar` — a work screen reaching into the inquiries
 * feature for a menu row would make the two features depend on each other for
 * styling, which is the coupling the directory move just removed.
 */
function MenuItem({
  children,
  disabled,
  onSelect,
}: {
  children: React.ReactNode;
  disabled?: boolean;
  onSelect: () => void;
}): React.JSX.Element {
  return (
    <DropdownMenu.Item
      disabled={disabled}
      onSelect={onSelect}
      className="flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-body13 text-fg outline-none data-[highlighted]:bg-subtle data-[disabled]:opacity-50"
    >
      {children}
    </DropdownMenu.Item>
  );
}

/**
 * THE SERVER'S JOIN, RESTATED — owner ruling F-103.
 *
 * `work-orders.service.ts` gates assignment on exactly one predicate:
 *
 *     SELECT u.id FROM users u
 *       JOIN user_country_roles ucr ON ucr.user_id = u.id
 *      WHERE u.id = ? AND ucr.country_id = ? AND u.status = 'active'
 *
 * …and answers 404 when it returns nothing. Two things follow, and both differ from
 * the inquiries rule this picker's `outOfScope` marker carries:
 *
 *  1. THERE IS NO GROUP-ADMIN EXEMPTION. That join has no `is_group_admin` branch —
 *     the service does not mention the flag at all. A group administrator holding no
 *     role row in this country is REFUSED here, while `isOutOfCountryScope` marks them
 *     in scope (correctly, for inquiries, where `grp:true` really does bypass country
 *     scope). Left to that marker this surface sent such an assignee SILENTLY into a
 *     404 — no confirm, no warning — which is why this predicate is not a copy of it.
 *  2. `status: 'active'` IS ALREADY GUARANTEED by the directory query
 *     (`directory/api.ts` passes `status: 'active'`), so it is not re-checked here.
 *     If that ever changes this predicate must grow the second half.
 *
 * The list this reads — `Colleague.countries` — is built from the same `roles[].country`
 * the API returns, so the client and the server are answering from one fact.
 *
 * NOT EXPORTED, deliberately, and for the reason `BulkBar`'s `countryList` gives: it is
 * behaviour, not contract, and `work-record.test.tsx` asserts it where it matters — at the
 * MUTATION, which must never be called for somebody the server would refuse. A test that
 * imported this function could pass while the picker sent the request anyway, which is the
 * false green `assign-colleague.test.tsx`'s own header warns a confirm dialog invites.
 * Exporting it would also add a `react-refresh/only-export-components` warning.
 */
function isAssignableHere(
  colleague: Pick<Colleague, 'countries'>,
  countryCode: CountryCode,
): boolean {
  return colleague.countries.includes(countryCode);
}

export function AssignPopover({
  countryCode,
  myUserId,
  canReadDirectory,
  busy,
  requireSerial = false,
  onAssign,
}: {
  countryCode: CountryCode;
  myUserId: string | null;
  /** `users.manage`, or a group admin. Without it `GET /users` answers 403. */
  canReadDirectory: boolean;
  busy: boolean;
  /**
   * v22-E2 — true for a GOVERNMENT-PATCH job with no serial on the order yet.
   * The API refuses such an assignment without `tally_serial` (owner ruling
   * 4), so the popover asks for it UP FRONT: a picker that lets the operator
   * choose a person and then 422s is the refusal arriving at the wrong
   * moment. The field appears only when it is load-bearing.
   */
  requireSerial?: boolean;
  onAssign: (userId: string, name: string | null, tallySerial?: string) => void;
}): React.JSX.Element {
  const [term, setTerm] = useState('');
  const debounced = useDebouncedTerm(term);
  const directory = useColleagues(countryCode, canReadDirectory, myUserId, debounced);
  /**
   * Non-null means the REFUSAL dialog is open (F-103). Nothing has been sent, and
   * unlike the confirm this replaced, nothing will be — there is no path from here
   * to a POST. It holds the person only so the dialog can name them.
   */
  const [refused, setRefused] = useState<Colleague | null>(null);
  /** v22-E2 — the patch job's compile target, typed before choosing a person. */
  const [serial, setSerial] = useState('');

  const serialMissing = requireSerial && serial.trim() === '';

  /*
   * ⚠ THIS BRANCHED ON `person.outOfScope` AND ASKED "Assign anyway?" (F-103). That is
   * the INQUIRIES verdict, where the server writes the assignment and flags it — so a
   * confirm there is a real question. Here the server REFUSES, so the confirm was asking
   * permission to send a request that could only 404: the operator pressed "Assign
   * anyway", the POST went, and the failure came back as "That work order does not
   * exist." A UI that contradicts its own server is worse than silence.
   *
   * Now the verdict is {@link isAssignableHere} — the server's own join — and an
   * unassignable person is REFUSED BEFORE ANYTHING IS SENT. `onAssign` is not called, so
   * no mutation is queued; the dialog that opens explains why rather than offering to
   * proceed. The row MARKER below asks the same question, so the badge and the refusal
   * can never disagree — they did under `outOfScope`, which flags an out-of-country
   * colleague but NOT a group administrator holding no role row here, whom the server
   * refuses just the same.
   */
  const choose = (person: Colleague): void => {
    if (!isAssignableHere(person, countryCode)) {
      setRefused(person);
      return;
    }
    onAssign(person.id, person.name, serial.trim() === '' ? undefined : serial.trim());
  };

  return (
    <>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger
          disabled={busy}
          className="flex h-8 items-center gap-1.5 rounded-sm border border-line bg-surface px-2.5 text-body13 font-medium text-fg hover:border-line-strong disabled:opacity-60"
        >
          {busy ? 'Assigning…' : 'Assign'}
          <ChevronDown aria-hidden="true" className="size-3.5" />
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="end"
            sideOffset={6}
            className={`z-50 ${directory === null ? 'w-[240px]' : 'w-[264px]'} rounded-md border border-line bg-surface p-1.5 shadow-[var(--crf-shadow-overlay)]`}
          >
            {requireSerial ? (
              /*
               * v22-E2 — the serial, asked where the act happens. `onKeyDown`
               * stops Radix's typeahead from stealing the keystrokes.
               */
              <div className="px-2 pb-1.5 pt-1">
                <label className="flex flex-col gap-1">
                  <span className="text-th uppercase text-fg-secondary">
                    Tally serial — the patch compiles against it
                  </span>
                  <input
                    value={serial}
                    maxLength={20}
                    placeholder="7XXXXXXXX"
                    onChange={(event) => {
                      setSerial(event.target.value);
                    }}
                    onKeyDown={(event) => {
                      event.stopPropagation();
                    }}
                    className="h-8 rounded-sm border border-rim bg-surface px-2 font-mono text-body13 text-fg focus:outline-none focus:ring-2 focus:ring-focus"
                  />
                </label>
                {serialMissing ? (
                  <p className="mt-1 text-label text-fg-secondary">
                    A government-patch job cannot be assigned without it.
                  </p>
                ) : null}
              </div>
            ) : null}
            {myUserId === null ? null : (
              <MenuItem disabled={serialMissing} onSelect={() => onAssign(myUserId, null, serial.trim() === '' ? undefined : serial.trim())}>
                Assign to me
              </MenuItem>
            )}

            {directory === null ? (
              // C5, unchanged: a picker fed by a route that will 403 is worse than
              // no picker, because the failure arrives as a broken menu.
              <p className="px-2 pb-1 pt-2 text-label text-fg-muted">
                Assigning to a colleague needs the user directory, which your account
                cannot read. You can still take this work yourself.
              </p>
            ) : (
              <>
                <DropdownMenu.Separator className="my-1.5 h-px bg-line" />
                <DropdownMenu.Label className="px-2 pb-1 text-label font-medium text-fg-muted">
                  Assign to a colleague
                </DropdownMenu.Label>

                {/* Keystrokes belong to the input, not to the menu's
                  * type-ahead-to-focus behaviour. Escape and Tab stay the menu's. */}
                <div
                  className="px-2 pb-1.5"
                  onKeyDown={(event) => {
                    if (event.key !== 'Escape' && event.key !== 'Tab') event.stopPropagation();
                  }}
                >
                  <input
                    type="search"
                    value={term}
                    autoComplete="off"
                    aria-label="Search colleagues"
                    placeholder="Search by name or email"
                    onChange={(event) => setTerm(event.target.value)}
                    className="w-full rounded-sm border border-line bg-surface px-2 py-1 text-body13 text-fg placeholder:text-fg-muted"
                  />
                </div>

                {directory.status === 'idle' ? (
                  <p className="px-2 py-1.5 text-body13 text-fg-muted">
                    Type at least {MIN_COLLEAGUE_SEARCH} characters to find a colleague.
                  </p>
                ) : directory.status === 'loading' ? (
                  <p className="px-2 py-1.5 text-body13 text-fg-muted">Searching…</p>
                ) : directory.status === 'error' ? (
                  <p className="px-2 py-1.5 text-body13 text-fg-muted">
                    The user directory could not be read, so this list is not the whole
                    story. Reopen this menu to try again.
                  </p>
                ) : directory.people.length === 0 ? (
                  <p className="px-2 py-1.5 text-body13 text-fg-muted">
                    No active account matches that.
                  </p>
                ) : (
                  <>
                    <div className="max-h-[228px] overflow-y-auto">
                      {directory.people.map((person) => (
                        <MenuItem key={person.id} disabled={serialMissing} onSelect={() => choose(person)}>
                          <span className="flex min-w-0 items-center gap-2">
                            <UserAvatar
                              name={person.name}
                              userId={person.id}
                              size={24}
                              decorative
                            />
                            <span className="flex min-w-0 flex-col">
                              <span className="truncate">{person.name}</span>
                              {/*
                                * ⚠ THIS READ `person.outOfScope` AND SAID "Not in {cc}"
                                * (F-103). That marker is the INQUIRIES verdict, and it shows
                                * NOTHING for a group administrator holding no role row here —
                                * who this surface's server refuses just the same. So the row
                                * that would fail gave the operator no signal at all, and the
                                * first news of it was a 404. It now asks the same question
                                * `choose` does. The wording is "no role in" rather than "not
                                * in": a group admin IS in every country in the sense the
                                * marker used to mean, and is still not assignable here.
                                */}
                              {isAssignableHere(person, countryCode) ? null : (
                                <span className="text-label text-warn-fg">
                                  No role in {countryCode}
                                </span>
                              )}
                            </span>
                          </span>
                        </MenuItem>
                      ))}
                    </div>
                    {directory.capped ? (
                      // RULE 11. A truncated list that does not admit it teaches the
                      // operator that a colleague has no account.
                      <p className="px-2 pb-1 pt-1.5 text-label text-fg-muted">
                        Showing the first {String(directory.people.length)} of{' '}
                        {String(directory.total)} matches. Type more to narrow it.
                      </p>
                    ) : null}
                  </>
                )}
              </>
            )}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>

      {refused === null ? null : (
        <CannotAssignDialog
          colleague={refused}
          countryCode={countryCode}
          onClose={() => setRefused(null)}
        />
      )}
    </>
  );
}

/**
 * THE REFUSAL, SHOWN INSTEAD OF A CONFIRM — owner ruling F-103.
 *
 * ⚠ THIS WAS `AssignAnywayDialog`, AND ITS DOCBLOCK GAVE THE DEFECT AS THE RATIONALE.
 * It read: "The server accepts the assignment regardless; that is exactly why the question
 * has to come first." It does not. `work-orders.service.ts` answers 404 when the person
 * named holds no role row in this order's own country — so the old dialog asked permission
 * to send a request that could only fail, told the operator the work "will be accepted",
 * and the refusal then arrived as "That work order does not exist" (`errors.ts` cannot tell
 * the two 404s apart; FILED, it needs a server discriminator that collides with a
 * deliberate anti-probe rule).
 *
 * THERE IS NO CONFIRM BUTTON, because there is nothing to proceed to. The dialog exists to
 * say WHY the choice was refused — a row marker alone leaves the operator guessing — and to
 * name the one thing that would change the answer. The copy states what is true of BOTH
 * refused cases: an out-of-country colleague, and a group administrator who holds no role
 * row here, whom the old marker did not flag at all.
 */
function CannotAssignDialog({
  colleague,
  countryCode,
  onClose,
}: {
  colleague: Colleague;
  countryCode: CountryCode;
  onClose: () => void;
}): React.JSX.Element {
  return (
    <Dialog.Root
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-ink/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[min(460px,calc(100vw-32px))] -translate-x-1/2 -translate-y-1/2 rounded-md border border-line bg-surface p-6 shadow-[var(--crf-shadow-overlay)]">
          <Dialog.Title className="text-h3 text-fg">Cannot assign this work</Dialog.Title>
          <Dialog.Description className="mt-1 text-label text-fg-muted">
            {colleague.name} holds no role in {countryCode}.
          </Dialog.Description>
          <div className="mt-4 flex flex-col gap-2 text-body13 text-fg">
            <p>
              Nothing was sent. A work order can only be handed to somebody who holds a role
              in its own country, so the server would refuse this assignment — it is not a
              warning that can be accepted.
            </p>
            <p className="text-fg-secondary">
              Ask an administrator for a {countryCode} role first if this is meant to be
              their work.
            </p>
          </div>
          <div className="mt-5 flex items-center justify-end gap-2">
            <Button onClick={onClose}>Close</Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
